import { InternalTransferTypeEnum } from '@interfaces/internal-transfer/internal-transfer.interface';
import { AccountTypes } from '@interfaces/models/account-types';
import { TransactionTypesEnum } from '@interfaces/models/selected-client-accounts.interface';
// `coo-transactions-common-library/dist/models` is a 95-module barrel, and importing enums through
// it makes every consumer load all 95. Each enum also has its own module, so deep imports pull in
// only what is used.
//
// The `type` keyword below is mandatory, not stylistic: under ts-jest's transpile-only mode a plain
// `import { SomeInterface }` cannot be elided, because TypeScript compiles each file in isolation
// and cannot tell a type from a value. Written as `import type`, it is erased at emit entirely.
import type {
  Address as CO0Address,
  ContactInformation,
  CostBasisPutRequestData,
  Employment,
  FromAccount,
  Holding,
  InformationFile,
  InternalTransferPayload,
  InvestmentGoals,
  Phone,
  TaxLot,
  ToAccount,
} from 'coo-transactions-common-library/dist/models';
import { AccountTypesFull } from 'coo-transactions-common-library/dist/models/account-types';
import { AmountIndicator } from 'coo-transactions-common-library/dist/models/amount-indicator';
import { SMNv2CostBasisMethods } from 'coo-transactions-common-library/dist/models/cost-basis.interface';
import { CostBasisMethod } from 'coo-transactions-common-library/dist/models/cost-basis-method';
import { DefectReasonCode } from 'coo-transactions-common-library/dist/models/defect-reason-code';
import { EmploymentStatus as CO0EmploymentStatus } from 'coo-transactions-common-library/dist/models/employment-status';
import { IncomeRange } from 'coo-transactions-common-library/dist/models/income-range';
import { InternalTransferTypeFullEnum } from 'coo-transactions-common-library/dist/models/internal-transfer';
import { InvestmentGoal } from 'coo-transactions-common-library/dist/models/investment-goal';
import { S3FileExtension } from 'coo-transactions-common-library/dist/models/s3-file-extension';
import { StateCode } from 'coo-transactions-common-library/dist/models/state-code';
import { TransactionTypes } from 'coo-transactions-common-library/dist/models/transaction';
import { TransferType } from 'coo-transactions-common-library/dist/models/transfer-types';

// Re-export library interfaces/types
export type {
  CO0Address,
  ContactInformation,
  CostBasisPutRequestData,
  Employment,
  FromAccount,
  Holding,
  InformationFile,
  InternalTransferPayload,
  InvestmentGoals,
  Phone,
  TaxLot,
  ToAccount,
};

// Re-export library enums (used as values at runtime)
export {
  AccountTypesFull,
  AmountIndicator,
  CO0EmploymentStatus,
  CostBasisMethod,
  DefectReasonCode,
  IncomeRange,
  InternalTransferTypeFullEnum,
  InvestmentGoal,
  S3FileExtension,
  SMNv2CostBasisMethods,
  StateCode,
  TransactionTypes,
  TransferType,
};

// ─── Mapper Helper Types ─────────────────────────────────────────────────────

export const AccountTypesToFullMap: Partial<Record<string, AccountTypesFull>> = {
  [AccountTypes.INDV]: AccountTypesFull.INDV,
  [AccountTypes.INDIVIDUAL]: AccountTypesFull.INDV,
  [AccountTypes.JOINT]: AccountTypesFull.JOINT,
  [AccountTypes.IRA]: AccountTypesFull.IRA,
  [AccountTypes.TRUST]: AccountTypesFull.TRUST,
  [AccountTypes.ORG]: AccountTypesFull.ORG,
  [AccountTypes.ESTATE]: AccountTypesFull.EMPTY, // Library's AccountTypesFull lacks ESTATE; using EMPTY as placeholder
  [AccountTypes.UGMA_UTMA]: AccountTypesFull.UGMA_UTMA,
};

export const TransferTypeToFullMap: Partial<
  Record<InternalTransferTypeEnum, InternalTransferTypeFullEnum>
> = {
  [InternalTransferTypeEnum.ENTIRE_ACCOUNT]: InternalTransferTypeFullEnum.ENTIRE_ACCOUNT,
  [InternalTransferTypeEnum.PARTIAL]: InternalTransferTypeFullEnum.PARTIAL,
  [InternalTransferTypeEnum.UNKNOWN]: InternalTransferTypeFullEnum.UNKNOWN,
};

export const IncomeLetterCodeMap: Record<string, IncomeRange> = {
  G: IncomeRange.LESS_THAN_20000,
  H: IncomeRange.BETWEEN_20000_AND_49999,
  I: IncomeRange.BETWEEN_50000_AND_74999,
  J: IncomeRange.BETWEEN_75000_AND_99999,
  K: IncomeRange.BETWEEN_100000_AND_499999,
  L: IncomeRange.BETWEEN_500000_AND_999999,
  M: IncomeRange.ABOVE_1000000,
};

export const InvestmentGoalKeyMap: Record<string, InvestmentGoal> = {
  CAP: InvestmentGoal.CAP,
  Income: InvestmentGoal.Income,
  Growth: InvestmentGoal.Growth,
  Speculation: InvestmentGoal.Speculation,
};

export const EmploymentStatusCodeMap: Record<string, CO0EmploymentStatus> = {
  E: CO0EmploymentStatus.EMPLOYED,
  SE: CO0EmploymentStatus.SELF_EMPLOYED,
  VE: CO0EmploymentStatus.VANGUARD_EMPLOYED,
  R: CO0EmploymentStatus.RETIRED,
  U: CO0EmploymentStatus.NOT_EMPLOYED,
};

export const TransactionTypesMap: Record<TransactionTypesEnum, TransactionTypes> = {
  [TransactionTypesEnum.EXISTING]: TransactionTypes.EXISTING,
  [TransactionTypesEnum.NEW_ACCOUNT]: TransactionTypes.NEW_ACCOUNT,
  [TransactionTypesEnum.FORCED_NEW_ACCOUNT]: TransactionTypes.FORCED_NEW_ACCOUNT,
  [TransactionTypesEnum.UNKNOWN]: TransactionTypes.UNKNOWN,
};

export const S3FileExtensionMap: Record<string, S3FileExtension> = Object.fromEntries(
  Object.values(S3FileExtension).map((ext) => [ext, ext]),
) as Record<string, S3FileExtension>;

export const CostBasisMethodMap: Record<string, CostBasisMethod> = {
  AvgCost: CostBasisMethod.AVERAGE_COST,
  FIFO: CostBasisMethod.FIFO,
  HIFO: CostBasisMethod.HIFO,
  MinTax: CostBasisMethod.MIN_TAX,
  SpecID: CostBasisMethod.SPECID,
  '': CostBasisMethod.NONE,
};
